import React from "react";
import { Link } from "react-router-dom";
import { Cookie, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, siteUrl } from "@/lib/seo";

const LAST_UPDATED = "12 mei 2026";

const openCookieSettings = () => {
  window.dispatchEvent(new Event("wasz:open-cookie-banner"));
};

const Cookies = () => {
  const { lang } = useLang();

  useSEO({
    title: lang === "nl"
      ? "Cookieverklaring — Wasz.nl"
      : "Cookie Policy — Wasz.nl",
    description: lang === "nl"
      ? "Welke cookies Wasz.nl plaatst, waarom, en hoe u uw toestemming kunt aanpassen of intrekken. Geen marketingcookies, geen tracking voor derden."
      : "Which cookies Wasz.nl places, why, and how to adjust or revoke your consent. No marketing cookies, no third-party tracking.",
    canonical: siteUrl("/cookies"),
    lang,
    jsonLd: [
      {
        id: "cookies-breadcrumb",
        data: breadcrumbJsonLd([
          { name: "Home", url: siteUrl("/") },
          { name: lang === "nl" ? "Cookieverklaring" : "Cookie Policy", url: siteUrl("/cookies") },
        ]),
      },
    ],
  });

  return (
    <main data-testid="cookies-page" className="min-h-screen pt-20 pb-24">
      <section className="mx-auto max-w-4xl px-6 lg:px-8">
        <div className="mb-12">
          <p className="section-overline mb-3 inline-flex items-center gap-2">
            <Cookie className="w-3.5 h-3.5" /> {lang === "nl" ? "Juridisch" : "Legal"}
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[1.05]">
            {lang === "nl" ? "Cookieverklaring" : "Cookie Policy"}
          </h1>
          <p className="mt-4 text-white/55 text-sm">
            {lang === "nl" ? "Laatst bijgewerkt: " : "Last updated: "}
            <span className="text-white/80 font-medium">{LAST_UPDATED}</span>
          </p>
          <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed">
            {lang === "nl"
              ? "Wij gebruiken cookies zo terughoudend mogelijk. Geen marketingcookies, geen tracking voor derden. In deze verklaring leest u precies welke cookies wij gebruiken en welke keuze u heeft."
              : "We use cookies as sparingly as possible. No marketing cookies, no third-party tracking. This policy describes exactly which cookies we use and what choice you have."}
          </p>
        </div>

        {/* What is a cookie */}
        <section className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7 sm:p-8 mb-10">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-3">
            {lang === "nl" ? "Wat zijn cookies?" : "What are cookies?"}
          </h2>
          <p className="text-white/65 text-sm sm:text-[15px] leading-relaxed">
            {lang === "nl"
              ? "Cookies zijn kleine tekstbestanden die een website op uw apparaat plaatst. Sommige zijn noodzakelijk voor de werking van de site, andere helpen ons om de website te verbeteren door anoniem te meten hoe hij gebruikt wordt."
              : "Cookies are small text files placed on your device by a website. Some are essential for the site to work; others help us improve the site by anonymously measuring how it is used."}
          </p>
        </section>

        {/* Cookie table */}
        <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-5">
          {lang === "nl" ? "Welke cookies gebruiken wij?" : "Which cookies do we use?"}
        </h2>

        <div className="overflow-x-auto rounded-2xl border border-white/10 mb-12">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a18] text-[11px] uppercase tracking-[0.16em] text-white/50 font-semibold">
              <tr>
                <th className="text-left px-5 py-3.5 font-semibold">{lang === "nl" ? "Categorie" : "Category"}</th>
                <th className="text-left px-5 py-3.5 font-semibold">{lang === "nl" ? "Doel" : "Purpose"}</th>
                <th className="text-left px-5 py-3.5 font-semibold">{lang === "nl" ? "Leverancier" : "Provider"}</th>
                <th className="text-left px-5 py-3.5 font-semibold">{lang === "nl" ? "Bewaartermijn" : "Lifetime"}</th>
                <th className="text-left px-5 py-3.5 font-semibold">{lang === "nl" ? "Toestemming" : "Consent"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-white/70">
              <tr className="bg-[#0F0F23]">
                <td className="px-5 py-4 font-semibold text-white">{lang === "nl" ? "Functioneel" : "Functional"}</td>
                <td className="px-5 py-4">{lang === "nl" ? "Onthoudt uw taalkeuze en uw cookietoestemming." : "Remembers your language and cookie consent choice."}</td>
                <td className="px-5 py-4">Wasz.nl</td>
                <td className="px-5 py-4">{lang === "nl" ? "Lokale opslag (permanent)" : "Local storage (persistent)"}</td>
                <td className="px-5 py-4"><span className="inline-flex items-center gap-1.5 text-[#10B981] text-xs font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />{lang === "nl" ? "Geen vereist" : "Not required"}</span></td>
              </tr>
              <tr className="bg-[#0F0F23]">
                <td className="px-5 py-4 font-semibold text-white">{lang === "nl" ? "Analytisch" : "Analytics"}</td>
                <td className="px-5 py-4">{lang === "nl" ? "Anoniem meten hoe deze website wordt gebruikt om de content te kunnen verbeteren." : "Anonymously measuring how this site is used so we can improve the content."}</td>
                <td className="px-5 py-4">Google Analytics 4 <span className="text-white/40">(_ga, _ga_*)</span></td>
                <td className="px-5 py-4">{lang === "nl" ? "Max. 14 maanden" : "Max. 14 months"}</td>
                <td className="px-5 py-4"><span className="inline-flex items-center gap-1.5 text-[#00D4FF] text-xs font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF]" />{lang === "nl" ? "Toestemming nodig" : "Consent required"}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* What we DON'T do */}
        <section className="rounded-2xl border border-[#10B981]/25 bg-[#10B981]/5 p-7 sm:p-8 mb-12">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-3">
            {lang === "nl" ? "Wat wij níet doen" : "What we do not do"}
          </h2>
          <ul className="space-y-2 text-sm sm:text-[15px] text-white/75">
            <li className="flex gap-3"><span className="text-[#10B981] font-bold">✓</span>{lang === "nl" ? "Geen advertentie- of marketingcookies." : "No advertising or marketing cookies."}</li>
            <li className="flex gap-3"><span className="text-[#10B981] font-bold">✓</span>{lang === "nl" ? "Geen tracking voor sociale media of remarketing." : "No social-media or remarketing tracking."}</li>
            <li className="flex gap-3"><span className="text-[#10B981] font-bold">✓</span>{lang === "nl" ? "Geen verkoop of doorverkoop van gegevens aan derden." : "No sale or onward sharing of data to third parties."}</li>
            <li className="flex gap-3"><span className="text-[#10B981] font-bold">✓</span>{lang === "nl" ? "Geen analytics-cookies voordat u expliciet toestemming geeft." : "No analytics cookies before you explicitly consent."}</li>
          </ul>
        </section>

        {/* Manage */}
        <section className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7 sm:p-8 mb-12">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-3">
            {lang === "nl" ? "Uw toestemming aanpassen" : "Manage your consent"}
          </h2>
          <p className="text-white/65 text-sm sm:text-[15px] leading-relaxed mb-5">
            {lang === "nl"
              ? "U kunt uw keuze op elk moment wijzigen. De cookiebanner verschijnt opnieuw zodra u op onderstaande knop klikt. Wijzigingen werken direct."
              : "You can change your choice at any time. The cookie banner reappears as soon as you click the button below. Changes apply immediately."}
          </p>
          <button
            type="button"
            data-testid="cookies-page-open-banner"
            onClick={openCookieSettings}
            className="inline-flex items-center gap-2 rounded-md bg-[#00D4FF] hover:bg-white text-[#050509] font-bold text-sm h-10 px-5 transition-colors"
          >
            <Cookie className="w-4 h-4" /> {lang === "nl" ? "Cookie-instellingen openen" : "Open cookie settings"}
          </button>
          <p className="text-white/45 text-xs mt-5 leading-relaxed">
            {lang === "nl"
              ? "U kunt cookies ook beheren via de instellingen van uw browser. Houd er rekening mee dat het verwijderen van cookies invloed kan hebben op de werking van deze en andere websites."
              : "You can also manage cookies through your browser settings. Note that removing cookies may affect how this and other websites work."}
          </p>
        </section>

        {/* Footer link */}
        <div className="mt-4 pt-8 border-t border-white/10 flex items-center justify-between text-sm">
          <Link to="/privacy" className="inline-flex items-center gap-2 text-white/70 hover:text-[#00D4FF]" data-testid="link-to-privacy">
            {lang === "nl" ? "Lees onze Privacyverklaring" : "Read our Privacy Policy"} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="text-white/50 hover:text-[#00D4FF]">
            {lang === "nl" ? "Contact" : "Contact"}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Cookies;
